// Third Party Imports
import {createSelector} from 'reselect';
import get from 'lodash/get';

// Artifactory
import {currencyFormatter} from '@homeaway/hacommons-i18n';

// Relative Imports
import {getTranslations, getListingExtended} from './store';
import {getLocale} from './system';
import {getSearchUrl, getSearchBaseUrl} from './searchUrl';
import {getListingRecommendedProperties, getPreferredLocation} from './listing';
import {labelMaker} from '../../server/utils/labelMaker';
import sanitizedCurrencySymbol from '../utils/sanitizedCurrencyFormat';
import {getImageUri} from '../utils/imageUri';

const fillHitLabels = (listingRecommendedProperties, translations, locale) => {
    return (
        listingRecommendedProperties.recommendedHits &&
        listingRecommendedProperties.recommendedHits.map((hit) => {
            const averageNightly =
                hit.price &&
                sanitizedCurrencySymbol(
                    currencyFormatter.formatNumber({
                        value: hit.price.averageNightly,
                        currencyCode: hit.price.currencyUnits,
                        locale
                    })
                );
            const bedroomsLabel =
                hit.bedrooms === 0
                    ? translations.studio
                    : `${hit.bedrooms}${translations.bedroomsAbbreviation}`;
            hit.cardTitle = `${bedroomsLabel} · ${translations.sleeps} ${hit.sleeps}`;
            hit.ratesLabel = averageNightly
                ? `${averageNightly} ${translations.averageNightly}`
                : '';
            return hit;
        })
    );
};

const getGeoTitle = createSelector(
    [getPreferredLocation, getTranslations],
    (preferredLocation, translations) => {
        const geoString = get(preferredLocation, 'name.simple');
        const composedString = labelMaker(translations.morePropertiesIn, geoString);

        return composedString;
    }
);

const getLoadingState = createSelector(
    [getListingExtended, getListingRecommendedProperties],
    (listingExtended, listingRecommendedProperties) => {
        const {
            recommendedPropertiesFetchFailed: fetchFailed,
            recommendedPropertiesFetchSuccess: fetchSuccess
        } = listingExtended;
        const hasHits = listingRecommendedProperties
            ? listingRecommendedProperties.recommendedHits &&
              listingRecommendedProperties.recommendedHits.length
            : false;

        return {
            isLoading: !fetchFailed && !fetchSuccess,
            isEmptyOrFailed: fetchFailed || (fetchSuccess && !hasHits)
        };
    }
);

const getRecommendedPropertiesThumbnails = createSelector(
    [getListingRecommendedProperties],
    (listingRecommendedProperties) => {
        const hits = get(listingRecommendedProperties, 'recommendedHits', []);
        const imagePrefix = get(listingRecommendedProperties, 'imagePrefix', '');
        const numHits = hits.length;
        const targetLength = 5;
        let thumbnails = [];

        if (numHits) {
            const imageUrlArray = [];

            hits.forEach((hit) => {
                const imagePath = getImageUri(get(hit, 'thumbnail.imageFiles', ''), 3);
                imageUrlArray.push(`${imagePrefix}${imagePath}`);
            });

            // ensure there are 5 images, repeat thumbnails if needed
            thumbnails = [...new Array(targetLength).keys()].map((val, i) => {
                return imageUrlArray[i] ? imageUrlArray[i] : imageUrlArray[i % numHits];
            });
        }

        return thumbnails;
    }
);

const getRecommendedProperties = createSelector(
    [
        getTranslations,
        getLoadingState,
        getListingRecommendedProperties,
        getLocale,
        getRecommendedPropertiesThumbnails,
        getSearchUrl,
        getSearchBaseUrl,
        getGeoTitle
    ],
    (
        translations,
        loadingState,
        listingRecommendedProperties,
        locale,
        recommendedPropertiesThumbnails,
        searchUrl,
        searchUrlBase,
        geoTitle
    ) => {
        if (listingRecommendedProperties) {
            listingRecommendedProperties.recommendedHits = fillHitLabels(
                listingRecommendedProperties,
                translations,
                locale
            );
        }

        return {
            ...listingRecommendedProperties,
            ...loadingState,
            recommendedPropertiesThumbnails,
            translations: {
                recommendationMessage: translations.recommendationMessage,
                rpNoReviews: translations.rpNoReviews,
                sleeps: translations.sleeps,
                bedroomsAbbreviation: translations.bedroomsAbbreviation,
                averageNightly: translations.averageNightly,
                seeMore: translations.seeMore,
                seeMoreProperties: translations.seeMoreProperties,
                studio: translations.studio,
                propertiesInThisGeo: geoTitle,
                viewMore: translations.more
            },
            searchUrl,
            searchUrlBase,
            locale
        };
    }
);

export default getRecommendedProperties;

export {
    getRecommendedProperties,
    getRecommendedPropertiesThumbnails,
    fillHitLabels,
    getGeoTitle,
    getLoadingState
};
